import { StatusV2, formatCurrency, getFriendlyDate } from "@vellum/ui";
import { useAuth } from "@vellum/micro-app-auth";
import { usePayroll } from "@/lib/payroll/PayrollProvider";
import { jobTotal } from "@/lib/payroll/mockData";

export function MyPayrollPage() {
  const { user } = useAuth();
  const {
    payrollWindow,
    myJobs,
    myStatementApproved,
    approveMyStatement,
    mgmtApprovedMyStatement,
  } = usePayroll();

  const totals = myJobs.reduce(
    (acc, job) => ({
      basePay: acc.basePay + job.basePay,
      bonus: acc.bonus + job.bonus,
      travelMiles: acc.travelMiles + job.travelMiles,
      mileageReimbursement: acc.mileageReimbursement + job.mileageReimbursement,
      total: acc.total + jobTotal(job),
    }),
    { basePay: 0, bonus: 0, travelMiles: 0, mileageReimbursement: 0, total: 0 },
  );

  const downloadable = mgmtApprovedMyStatement;

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4 print:hidden">
        <div>
          <h1 className="mb-2 text-3xl font-bold tracking-tight text-green-700">
            My Payroll
          </h1>
          <p className="m-0 max-w-xl text-sm text-gray-600">
            Review your completed jobs for the current payroll window, approve
            your statement, and download it as a PDF once management has
            approved.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {myStatementApproved ? null : (
            <button
              type="button"
              onClick={approveMyStatement}
              className="rounded-md bg-green-700 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700/90"
            >
              Approve my statement
            </button>
          )}
          <button
            type="button"
            onClick={() => window.print()}
            disabled={!downloadable}
            title={
              downloadable
                ? "Download your payroll statement as a PDF"
                : "Available after management approval"
            }
            className="rounded-md border border-green-700 bg-white px-4 py-2 text-sm font-medium text-green-700 transition-colors hover:bg-green-700/5 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Download PDF
          </button>
        </div>
      </div>

      {/* Payroll statement — this section is what prints to PDF. */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm print:rounded-none print:border-0 print:shadow-none">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4 border-b border-gray-200 pb-5">
          <div>
            <p className="m-0 text-lg font-semibold tracking-tight text-green-700">
              Vellum Health — Payroll Statement
            </p>
            <p className="m-0 mt-1 text-sm text-gray-600">
              {user?.name ?? user?.email}
            </p>
            <p className="m-0 mt-0.5 text-sm text-gray-600">
              Window: {getFriendlyDate(payrollWindow.start)} –{" "}
              {getFriendlyDate(payrollWindow.end)}
            </p>
          </div>
          <div className="text-right text-sm">
            <div className="mb-1.5">
              <span className="mr-2 text-gray-500">Your review:</span>
              <StatusV2 status={myStatementApproved ? "approved" : "pending"} />
            </div>
            <div>
              <span className="mr-2 text-gray-500">Management:</span>
              <StatusV2
                status={mgmtApprovedMyStatement ? "approved" : "pending"}
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-xs uppercase text-gray-500">
                <th className="px-3 py-2.5 font-semibold">Job</th>
                <th className="px-3 py-2.5 font-semibold">Completed</th>
                <th className="px-3 py-2.5 font-semibold">Facility</th>
                <th className="px-3 py-2.5 font-semibold">Procedure</th>
                <th className="px-3 py-2.5 text-right font-semibold">
                  Base pay
                </th>
                <th className="px-3 py-2.5 text-right font-semibold">Bonus</th>
                <th className="px-3 py-2.5 text-right font-semibold">
                  Travel mi
                </th>
                <th className="px-3 py-2.5 text-right font-semibold">
                  Mileage
                </th>
                <th className="px-3 py-2.5 text-right font-semibold">Total</th>
              </tr>
            </thead>
            <tbody>
              {myJobs.map((job) => (
                <tr
                  key={job.id}
                  className="border-b border-gray-100 last:border-b-0"
                >
                  <td className="px-3 py-2.5 font-medium text-green-700">
                    {job.id}
                  </td>
                  <td className="px-3 py-2.5 text-gray-700">
                    {getFriendlyDate(job.completedAt)}
                  </td>
                  <td className="px-3 py-2.5 text-gray-700">{job.facility}</td>
                  <td className="px-3 py-2.5 text-gray-700">{job.procedure}</td>
                  <td className="px-3 py-2.5 text-right text-gray-700">
                    {formatCurrency(job.basePay)}
                  </td>
                  <td className="px-3 py-2.5 text-right text-gray-700">
                    {job.bonus > 0 ? formatCurrency(job.bonus) : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-right text-gray-700">
                    {job.travelMiles.toFixed(1)}
                  </td>
                  <td className="px-3 py-2.5 text-right text-gray-700">
                    {formatCurrency(job.mileageReimbursement)}
                  </td>
                  <td className="px-3 py-2.5 text-right font-medium text-green-700">
                    {formatCurrency(jobTotal(job))}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-300 font-semibold text-green-700">
                <td className="px-3 py-3" colSpan={4}>
                  Totals ({myJobs.length} jobs)
                </td>
                <td className="px-3 py-3 text-right">
                  {formatCurrency(totals.basePay)}
                </td>
                <td className="px-3 py-3 text-right">
                  {formatCurrency(totals.bonus)}
                </td>
                <td className="px-3 py-3 text-right">
                  {totals.travelMiles.toFixed(1)}
                </td>
                <td className="px-3 py-3 text-right">
                  {formatCurrency(totals.mileageReimbursement)}
                </td>
                <td className="px-3 py-3 text-right">
                  {formatCurrency(totals.total)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <p className="mb-0 mt-5 text-xs text-gray-500">
          Questions about this statement? Contact your region admin. Amounts
          shown are mock data pending payroll API support.
        </p>
      </div>
    </div>
  );
}
